/**
 * Canonical Ink Model (CIM) v1 TypeScript Contracts & Validation.
 * Parity with contracts/cim-v1.schema.json and packages/core contracts.py.
 */

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

export const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
export const HASH_SHA256_RE = /^[0-9a-fA-F]{64}$/;

export interface PageSpec {
  width: number;
  height: number;
  unit: 'px';
  space: 'processed-image';
}

export interface SourceSpec {
  original_to_processed: number[];
  source_deleted: boolean;
  original_asset_id?: string | null;
  normalized_asset_id?: string | null;
  prepared_asset_id?: string | null;
}

export interface Ring {
  points: [number, number][];
}

export interface CompoundFill {
  id: string;
  kind: 'compound_fill';
  rings: Ring[];
  fill_rule: 'evenodd' | 'nonzero';
  color: string;
  opacity: number;
  transform: [number, number, number, number, number, number];
  group_id?: string | null;
  source_kind: 'reconstructed' | 'user' | 'imported';
}

export interface CenterlineStroke {
  id: string;
  kind: 'centerline_stroke';
  points: [number, number][];
  width: number;
  color: string;
  opacity: number;
  transform: [number, number, number, number, number, number];
  pressure?: number[] | null;
  timestamps?: number[] | null;
  group_id?: string | null;
  /**
   * 'imported' marks SVG-imported strokes (no sensor data, no model run);
   * see docs/adr/ADR-0005-cim-imported-stroke-provenance.md.
   */
  source_kind: 'user' | 'reconstructed' | 'imported';
}

export type InkObject = CompoundFill | CenterlineStroke;

export interface ModelProvenance {
  conversion_id: string;
  model_version: string;
  model_manifest_hash: string;
  preprocess_version: string;
  vectorizer_version: string;
  kind?: never;
}

export interface UserDeclaration {
  confirmed: boolean;
  declaration_time?: string;
  content_hash?: string;
}

export interface UserProvenance {
  kind: 'user';
  editor_version: 'tablet-notebook-1';
}

export type DocumentProvenance = ModelProvenance | UserProvenance;

export interface ReviewSpec {
  requirement: string;
  assessment_kind: 'heuristic_uncalibrated' | 'none' | 'calibrated_beta';
  user_declaration?: UserDeclaration | null;
}

export interface CIMDocument {
  schema_version: 'cim-1.0' | 'cim-1.1';
  document_id: string;
  revision_id: string;
  parent_revision_id?: string | null;
  page: PageSpec;
  source: SourceSpec;
  objects: InkObject[];
  provenance: DocumentProvenance;
  review: ReviewSpec;
}

/** Fixed logical drawing space for blank notebook pages (v1.1). */
export const BLANK_PAGE_WIDTH = 1600;
export const BLANK_PAGE_HEIGHT = 2263;

/**
 * Factory for the canonical blank notebook page (cim-1.1, user provenance).
 * No model ran: no model hash/version may be fabricated. All asset refs are
 * null and objects is empty; pressure/timestamps stay null by construction.
 */
export function createBlankCIMDocument(documentId: string, revisionId: string): CIMDocument {
  return {
    schema_version: 'cim-1.1',
    document_id: documentId,
    revision_id: revisionId,
    parent_revision_id: null,
    page: {
      width: BLANK_PAGE_WIDTH,
      height: BLANK_PAGE_HEIGHT,
      unit: 'px',
      space: 'processed-image',
    },
    source: {
      original_to_processed: [1, 0, 0, 0, 1, 0, 0, 0, 1],
      source_deleted: false,
      original_asset_id: null,
      normalized_asset_id: null,
      prepared_asset_id: null,
    },
    objects: [],
    provenance: {
      kind: 'user',
      editor_version: 'tablet-notebook-1',
    },
    review: {
      requirement: 'none',
      assessment_kind: 'none',
      user_declaration: null,
    },
  };
}

export function validateColor(color: unknown): string {
  if (typeof color !== 'string' || !HEX_COLOR_RE.test(color)) {
    throw new ValidationError(`Invalid hex color: ${String(color)}`);
  }
  return color;
}

export function validateTransform(t: unknown): [number, number, number, number, number, number] {
  if (!Array.isArray(t) || t.length !== 6) {
    throw new ValidationError(`Transform must be 6 numbers, got: ${JSON.stringify(t)}`);
  }
  for (const v of t) {
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      throw new ValidationError(`Transform contains non-finite number: ${v}`);
    }
  }
  const [a, b, c, d, e, f] = t;
  const det = a * d - b * c;
  if (Math.abs(det) < 1e-12) {
    throw new ValidationError(`Singular affine transform (determinant is zero: ${det})`);
  }
  return [a, b, c, d, e, f];
}

export function validateRing(ring: unknown): Ring {
  if (!ring || typeof ring !== 'object') {
    throw new ValidationError('Ring must be an object');
  }
  const r = ring as Record<string, unknown>;
  if (!Array.isArray(r.points) || r.points.length < 3) {
    throw new ValidationError('Ring must have at least 3 points');
  }
  const points: [number, number][] = [];
  for (const pt of r.points) {
    if (!Array.isArray(pt) || pt.length !== 2) {
      throw new ValidationError(`Ring point must be [x, y], got: ${JSON.stringify(pt)}`);
    }
    const [x, y] = pt;
    if (typeof x !== 'number' || !Number.isFinite(x) || typeof y !== 'number' || !Number.isFinite(y)) {
      throw new ValidationError(`Ring point coordinates must be finite numbers: [${x}, ${y}]`);
    }
    points.push([x, y]);
  }
  return { points };
}

export function validateCompoundFill(obj: unknown): CompoundFill {
  if (!obj || typeof obj !== 'object') {
    throw new ValidationError('CompoundFill must be an object');
  }
  const o = obj as Record<string, unknown>;
  if (typeof o.id !== 'string' || o.id.length < 1) {
    throw new ValidationError('CompoundFill id must be non-empty string');
  }
  if (o.kind !== 'compound_fill') {
    throw new ValidationError(`CompoundFill kind must be 'compound_fill', got '${String(o.kind)}'`);
  }
  if (!Array.isArray(o.rings) || o.rings.length < 1) {
    throw new ValidationError('CompoundFill must have at least 1 ring');
  }
  const rings = o.rings.map(validateRing);
  const color = validateColor(o.color);
  const fill_rule = o.fill_rule;
  if (fill_rule !== 'evenodd' && fill_rule !== 'nonzero') {
    throw new ValidationError(`Invalid fill_rule: ${String(fill_rule)}`);
  }
  const opacity = typeof o.opacity === 'number' ? o.opacity : 1.0;
  if (opacity < 0 || opacity > 1 || !Number.isFinite(opacity)) {
    throw new ValidationError(`Opacity must be between 0 and 1, got ${opacity}`);
  }
  const transform = validateTransform(o.transform);
  const source_kind = o.source_kind;
  if (source_kind !== 'reconstructed' && source_kind !== 'user' && source_kind !== 'imported') {
    throw new ValidationError(`Invalid source_kind: ${String(source_kind)}`);
  }

  return {
    id: o.id,
    kind: 'compound_fill',
    rings,
    fill_rule,
    color,
    opacity,
    transform,
    group_id: typeof o.group_id === 'string' ? o.group_id : null,
    source_kind,
  };
}

export function validateCenterlineStroke(obj: unknown): CenterlineStroke {
  if (!obj || typeof obj !== 'object') {
    throw new ValidationError('CenterlineStroke must be an object');
  }
  const o = obj as Record<string, unknown>;
  if (typeof o.id !== 'string' || o.id.length < 1) {
    throw new ValidationError('CenterlineStroke id must be non-empty string');
  }
  if (o.kind !== 'centerline_stroke') {
    throw new ValidationError(`CenterlineStroke kind must be 'centerline_stroke', got '${String(o.kind)}'`);
  }
  if (!Array.isArray(o.points) || o.points.length < 2) {
    throw new ValidationError('CenterlineStroke must have at least 2 points');
  }
  const points: [number, number][] = [];
  for (const pt of o.points) {
    if (!Array.isArray(pt) || pt.length !== 2) {
      throw new ValidationError(`Stroke point must be [x, y], got: ${JSON.stringify(pt)}`);
    }
    const [x, y] = pt;
    if (typeof x !== 'number' || !Number.isFinite(x) || typeof y !== 'number' || !Number.isFinite(y)) {
      throw new ValidationError(`Stroke point coordinates must be finite numbers: [${x}, ${y}]`);
    }
    points.push([x, y]);
  }

  if (typeof o.width !== 'number' || o.width <= 0 || !Number.isFinite(o.width)) {
    throw new ValidationError(`Stroke width must be positive finite number, got ${String(o.width)}`);
  }

  const color = validateColor(o.color);
  const opacity = typeof o.opacity === 'number' ? o.opacity : 1.0;
  if (opacity < 0 || opacity > 1 || !Number.isFinite(opacity)) {
    throw new ValidationError(`Opacity must be between 0 and 1, got ${opacity}`);
  }
  const transform = validateTransform(o.transform);
  const source_kind = o.source_kind;
  if (source_kind !== 'user' && source_kind !== 'reconstructed' && source_kind !== 'imported') {
    throw new ValidationError(`Invalid stroke source_kind: ${String(source_kind)}`);
  }

  return {
    id: o.id,
    kind: 'centerline_stroke',
    points,
    width: o.width,
    color,
    opacity,
    transform,
    pressure: Array.isArray(o.pressure) ? o.pressure.map(Number) : null,
    timestamps: Array.isArray(o.timestamps) ? o.timestamps.map(Number) : null,
    group_id: typeof o.group_id === 'string' ? o.group_id : null,
    source_kind,
  };
}

export function validateInkObject(obj: unknown): InkObject {
  if (!obj || typeof obj !== 'object') {
    throw new ValidationError('InkObject must be an object');
  }
  const kind = (obj as Record<string, unknown>).kind;
  if (kind === 'compound_fill') {
    return validateCompoundFill(obj);
  }
  if (kind === 'centerline_stroke') {
    return validateCenterlineStroke(obj);
  }
  throw new ValidationError(`Unknown InkObject kind: ${String(kind)}`);
}

export function validatePage(page: unknown): PageSpec {
  if (!page || typeof page !== 'object') {
    throw new ValidationError('PageSpec must be an object');
  }
  const p = page as Record<string, unknown>;
  if (typeof p.width !== 'number' || !Number.isInteger(p.width) || p.width < 1) {
    throw new ValidationError(`Page width must be integer >= 1, got ${String(p.width)}`);
  }
  if (typeof p.height !== 'number' || !Number.isInteger(p.height) || p.height < 1) {
    throw new ValidationError(`Page height must be integer >= 1, got ${String(p.height)}`);
  }
  if (p.unit !== 'px') {
    throw new ValidationError(`Page unit must be 'px', got '${String(p.unit)}'`);
  }
  if (p.space !== 'processed-image') {
    throw new ValidationError(`Page space must be 'processed-image', got '${String(p.space)}'`);
  }
  return {
    width: p.width,
    height: p.height,
    unit: 'px',
    space: 'processed-image',
  };
}

export function validateSource(source: unknown): SourceSpec {
  if (!source || typeof source !== 'object') {
    throw new ValidationError('SourceSpec must be an object');
  }
  const s = source as Record<string, unknown>;
  if (!Array.isArray(s.original_to_processed) || s.original_to_processed.length !== 9) {
    throw new ValidationError('original_to_processed must be array of 9 numbers');
  }
  for (const v of s.original_to_processed) {
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      throw new ValidationError('original_to_processed contains non-finite values');
    }
  }
  if (typeof s.source_deleted !== 'boolean') {
    throw new ValidationError('source_deleted must be boolean');
  }
  return {
    original_to_processed: s.original_to_processed.map(Number),
    source_deleted: s.source_deleted,
    original_asset_id: typeof s.original_asset_id === 'string' ? s.original_asset_id : null,
    normalized_asset_id: typeof s.normalized_asset_id === 'string' ? s.normalized_asset_id : null,
    prepared_asset_id: typeof s.prepared_asset_id === 'string' ? s.prepared_asset_id : null,
  };
}

export function validateProvenance(prov: unknown): DocumentProvenance {
  if (!prov || typeof prov !== 'object') {
    throw new ValidationError('Provenance must be an object');
  }
  const p = prov as Record<string, unknown>;
  // cim-1.1 user provenance branch (blank pages)
  if (p.kind === 'user') {
    if (p.editor_version !== 'tablet-notebook-1') {
      throw new ValidationError(
        `Invalid user provenance editor_version: ${String(p.editor_version)}`
      );
    }
    const allowed = new Set(['kind', 'editor_version']);
    for (const key of Object.keys(p)) {
      if (!allowed.has(key)) {
        throw new ValidationError(`Unknown field in user provenance: '${key}'`);
      }
    }
    return { kind: 'user', editor_version: 'tablet-notebook-1' };
  }
  for (const field of ['conversion_id', 'model_version', 'model_manifest_hash', 'preprocess_version', 'vectorizer_version']) {
    if (typeof p[field] !== 'string' || (p[field] as string).length < 1) {
      throw new ValidationError(`Provenance field '${field}' must be non-empty string`);
    }
  }
  if (!HASH_SHA256_RE.test(p.model_manifest_hash as string)) {
    throw new ValidationError(`Invalid model_manifest_hash: ${String(p.model_manifest_hash)}`);
  }
  return {
    conversion_id: p.conversion_id as string,
    model_version: p.model_version as string,
    model_manifest_hash: p.model_manifest_hash as string,
    preprocess_version: p.preprocess_version as string,
    vectorizer_version: p.vectorizer_version as string,
  };
}

export function validateReview(review: unknown): ReviewSpec {
  if (!review || typeof review !== 'object') {
    throw new ValidationError('ReviewSpec must be an object');
  }
  const r = review as Record<string, unknown>;
  if (typeof r.requirement !== 'string') {
    throw new ValidationError('Review requirement must be string');
  }
  const kind = r.assessment_kind;
  if (kind !== 'heuristic_uncalibrated' && kind !== 'none' && kind !== 'calibrated_beta') {
    throw new ValidationError(`Invalid assessment_kind: ${String(kind)}`);
  }
  let user_declaration: UserDeclaration | null = null;
  if (r.user_declaration && typeof r.user_declaration === 'object') {
    const ud = r.user_declaration as Record<string, unknown>;
    user_declaration = {
      confirmed: Boolean(ud.confirmed),
      declaration_time: typeof ud.declaration_time === 'string' ? ud.declaration_time : undefined,
      content_hash: typeof ud.content_hash === 'string' ? ud.content_hash : undefined,
    };
  }
  return {
    requirement: r.requirement,
    assessment_kind: kind,
    user_declaration,
  };
}

export function validateCIMDocument(doc: unknown): CIMDocument {
  if (!doc || typeof doc !== 'object') {
    throw new ValidationError('CIMDocument must be an object');
  }
  const d = doc as Record<string, unknown>;
  if (d.schema_version !== 'cim-1.0' && d.schema_version !== 'cim-1.1') {
    throw new ValidationError(
      `Invalid schema_version: '${String(d.schema_version)}', expected 'cim-1.0' or 'cim-1.1'`
    );
  }
  if (typeof d.document_id !== 'string' || d.document_id.length < 1) {
    throw new ValidationError('document_id must be non-empty string');
  }
  if (typeof d.revision_id !== 'string' || d.revision_id.length < 1) {
    throw new ValidationError('revision_id must be non-empty string');
  }

  const page = validatePage(d.page);
  const source = validateSource(d.source);
  if (!Array.isArray(d.objects)) {
    throw new ValidationError('objects must be an array');
  }
  const objects = d.objects.map(validateInkObject);
  const provenance = validateProvenance(d.provenance);
  const review = validateReview(d.review);

  // User provenance is only valid for sourceless pages: no photo/model ran on
  // this page. Object origins are orthogonal: 'user' (stylus) and 'imported'
  // (SVG interchange) are both honest on a page with no source assets;
  // 'reconstructed' implies a photo-derived model run and is rejected.
  if (provenance.kind === 'user') {
    const sourceless =
      source.original_asset_id === null &&
      source.normalized_asset_id === null &&
      source.prepared_asset_id === null;
    const honestOrigins = objects.every(
      (o) => o.source_kind === 'user' || o.source_kind === 'imported'
    );
    if (!sourceless || !honestOrigins) {
      throw new ValidationError(
        'User provenance requires null source asset ids and all objects with source_kind=user or imported'
      );
    }
  }

  return {
    schema_version: d.schema_version,
    document_id: d.document_id,
    revision_id: d.revision_id,
    parent_revision_id: typeof d.parent_revision_id === 'string' ? d.parent_revision_id : null,
    page,
    source,
    objects,
    provenance,
    review,
  };
}

/**
 * Strip transient runtime/UI fields (e.g. x, y, scale) ensuring strictly conforming CIM v1 schema.
 */
export function sanitizeCIMDocument(doc: CIMDocument): CIMDocument {
  const copy: CIMDocument = structuredClone(doc);
  copy.objects = copy.objects.map((obj: any) => {
    if (obj.kind === 'compound_fill') {
      const clean: CompoundFill = {
        id: obj.id,
        kind: 'compound_fill',
        rings: (obj.rings || []).map((r: any) => ({
          points: (r.points || []).map((p: any) => [p[0], p[1]] as [number, number]),
        })),
        fill_rule: obj.fill_rule || 'evenodd',
        color: obj.color,
        opacity: typeof obj.opacity === 'number' ? obj.opacity : 1.0,
        transform: obj.transform,
        group_id: obj.group_id ?? null,
        source_kind: obj.source_kind || 'reconstructed',
      };
      return clean;
    }
    if (obj.kind === 'centerline_stroke') {
      const clean: CenterlineStroke = {
        id: obj.id,
        kind: 'centerline_stroke',
        points: (obj.points || []).map((p: any) => [p[0], p[1]] as [number, number]),
        width: obj.width,
        color: obj.color,
        opacity: typeof obj.opacity === 'number' ? obj.opacity : 1.0,
        transform: obj.transform,
        pressure: obj.pressure,
        timestamps: obj.timestamps,
        group_id: obj.group_id ?? null,
        source_kind: obj.source_kind || 'user',
      };
      return clean;
    }
    return obj;
  });
  return copy;
}
